import {MenuItem} from 'primeng/api';

/** Роли пользователя, от которых зависит состав меню (IdentityService.isAdmin/isManager/isSaleManager). */
export interface NavigationRoles {
  admin: boolean;
  manager: boolean;
  saleManager: boolean;
}

/**
 * Модель бокового меню (перенесена из app.component.ts, PAR-12961). Верхний уровень — Orders (пункт без группы,
 * первым — как в утверждённом макете) и группы; у групп нет routerLink, их пункты — в `items`. Видимость по ролям — та
 * же, что была у p-menubar: группа скрыта целиком, отдельные пункты — своим `visible`.
 */
export function buildNavigation(roles: NavigationRoles): MenuItem[] {
  const {admin, manager, saleManager} = roles;
  return [
    {label: 'Orders', icon: 'pi pi-shopping-cart', routerLink: '/orders', visible: admin || manager || saleManager},
    {
      label: 'Catalog',
      visible: admin || manager,
      items: [
        {label: 'Fonts', icon: 'pi pi-align-left', routerLink: '/catalog/font'},
        {label: 'Ranks management', icon: 'pi pi-list', routerLink: '/catalog/ranks'},
        {label: 'Specials', icon: 'pi pi-star', routerLink: '/catalog/specials'},
      ],
    },
    {
      label: 'Store',
      visible: admin || manager,
      items: [
        {label: 'Sales Sources', icon: 'pi pi-sitemap', routerLink: '/store/sales-sources', visible: admin},
        {label: 'License Policy Metadata', icon: 'pi pi-sliders-h', routerLink: '/store/license-policy-meta', visible: admin || manager},
        {label: 'Store licenses', icon: 'pi pi-id-card', routerLink: '/store/standard-licenses', visible: admin || manager},
        {label: 'Licensors', icon: 'pi pi-building', routerLink: '/store/licensors', visible: admin},
      ],
    },
    {
      label: 'Reports',
      visible: admin,
      items: [
        {label: 'Royalty', icon: 'pi pi-percentage', routerLink: '/reports/royalty', visible: admin},
        {label: 'Sales', icon: 'pi pi-chart-bar', routerLink: '/reports/sales', visible: admin},
        // Только три пункта — как в утверждённом макете reports-menu. Отчёты площадок и модерация
        // каталога живут ВНУТРИ страницы источника (Sources processing → источник → вкладки),
        // а не отдельными пунктами: это стадии одного процесса, а не разные разделы.
        {label: 'Sources processing', icon: 'pi pi-database', routerLink: '/reports/sources-processing', visible: admin},
      ],
    },
    {
      label: 'Management',
      visible: admin || manager,
      items: [
        {label: 'Users', icon: 'pi pi-users', routerLink: '/management/users', visible: admin},
        {label: 'Partners', icon: 'pi pi-briefcase', routerLink: '/management/organizations', visible: admin},
      ],
    },
  ];
}

/** Пункт или группа показываются, если `visible` не выставлен в false (соглашение MenuItem PrimeNG). */
const shown = (item: MenuItem): boolean => item.visible !== false;

/**
 * Меню к показу: скрытые группы и пункты убраны, группа без видимых пунктов не показывается (у менеджера группа
 * Reports скрыта целиком, у Management видимых пунктов нет — пустая подпись группы в боковом меню не рисуется).
 */
export function visibleNavigation(menu: MenuItem[]): MenuItem[] {
  return menu
    .filter(shown)
    .map(entry => (entry.items ? {...entry, items: entry.items.filter(shown)} : entry))
    .filter(entry => !entry.items || entry.items.length > 0);
}

/** Крошка верхней полосы: раздел (группа меню, `null` у Orders) и страница. */
export interface CrumbPath {
  section: string | null;
  page: string;
}

interface CrumbEntry extends CrumbPath {
  path: string;
}

/**
 * Маршруты вне меню. Сборка заказа — шаг работы с заказами; легаси-адреса отчётов (catalog-moderation,
 * external-reports, reports.routes.ts) перенаправляют на страницу источника — крошка та же, что у неё.
 */
const EXTRA_CRUMBS: CrumbEntry[] = [
  {path: '/order-assembly', section: 'Orders', page: 'Order assembly'},
  {path: '/reports/catalog-moderation', section: 'Reports', page: 'Sources processing'},
  {path: '/reports/external-reports', section: 'Reports', page: 'Sources processing'},
];

/** Все пункты меню (без учёта ролей: крошка описывает адрес, а доступ к нему проверяют guard'ы маршрутов). */
function menuCrumbs(): CrumbEntry[] {
  const all = buildNavigation({admin: true, manager: true, saleManager: true});
  return all.flatMap(entry =>
    entry.items
      ? entry.items.map(item => ({path: item.routerLink as string, section: entry.label ?? null, page: item.label ?? ''}))
      : [{path: entry.routerLink as string, section: null, page: entry.label ?? ''}],
  );
}

const CRUMBS: CrumbEntry[] = [...menuCrumbs(), ...EXTRA_CRUMBS];

/**
 * Крошка по адресу: запись с самым длинным путём, который совпадает с началом адреса по границе сегмента
 * (`/store/licensors/5` → Licensors; `/orders-x` не совпадает с `/orders`). Query и fragment отбрасываются.
 * Адрес вне меню и без явной записи (главная `/`) — `null`: в полосе остаётся только «домой».
 */
export function breadcrumbFor(url: string): CrumbPath | null {
  const best = crumbEntryFor(url);
  return best ? {section: best.section, page: best.page} : null;
}

/** Адрес пункта меню той же крошки (`/reports/sources-processing/3/runs` → `/reports/sources-processing`). */
export function crumbLinkFor(url: string): string | null {
  return crumbEntryFor(url)?.path ?? null;
}

function crumbEntryFor(url: string): CrumbEntry | null {
  const path = url.split(/[?#]/, 1)[0];
  let best: CrumbEntry | null = null;
  for (const entry of CRUMBS) {
    const matches = path === entry.path || path.startsWith(`${entry.path}/`);
    if (matches && (!best || entry.path.length > best.path.length)) best = entry;
  }
  return best;
}
